import React, { useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { observer } from "mobx-react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import { useStore } from "@roc/client-portal-shared/hooks";
import { LeadDetails } from "../../leadDetails/leadDetails";
import { BorrowerDetailedScreen } from "../../borrowerDetails/borrowerDetailedScreen";
import { LenderDetailedScreen } from "../../lenderDetails/lenderDetailedScreen";
import {
  BORROWER_OBJECT_TYPE,
  LEAD_OBJECT_TYPE,
  TPO_OBJECT_TYPE,
  BROKER_OBJECT_TYPE,
} from "../../leads/constants/constants";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    peopleLayoutContainer: {
      display: "flex",
      width: "100%",
      height: "80vh",
      border: "1px solid #ccc",
      overflow: "hidden",
    },
    peopleList: {
      width: "18%",
      borderRight: "1px solid #ccc",
      overflowY: "auto",
      margin: 0,
      padding: 0,
      position: "relative",
      height: "100%",
    },
    peopleListH3: {
      marginLeft: "10px",
      position: "sticky",
      top: 0,
      backgroundColor: "#fff",
      zIndex: 1,
      padding: "10px 0",
    },
    peopleListUl: {
      listStyle: "none",
      padding: 0,
      margin: 0,
    },
    peopleListLi: {
      padding: "10px",
      cursor: "pointer",
      borderBottom: "1px solid #eee",
      display: "flex",
      alignItems: "center",
      backgroundColor: "#fff",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: "#f9f9f9",
      },
    },
    peopleDetail: {
      flex: 1,
      padding: "20px",
      overflowY: "auto",
      height: "100%",
      maxHeight: "80vh",
    },
    noSelectionMessage: {
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "#666",
      fontSize: "1.1rem",
    },
  })
);

interface ProspectsViewerProps {
  prospects: any[];
  recordType: string;
}

export const ProspectsViewer = observer(({ prospects, recordType }: ProspectsViewerProps) => {
  const classes = useStyles();
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const { salesforceLeadStore } = useStore();

  const getCorrectId = (data: any) => {
    if (data?.cadenceContactInfo?.leadId) return data?.cadenceContactInfo?.leadId;
    if (data?.cadenceContactInfo?.contactId) return data?.cadenceContactInfo?.contactId;
    return null;
  };

  const handleSelectPerson = (person: any) => {
    setSelectedPerson(person);
  };

  const renderDetails = () => {
    if (!selectedPerson) return null;

    const cadenceInfo = selectedPerson?.cadenceContactInfo;
    const id = cadenceInfo?.leadId ?? cadenceInfo?.contactId;
    const entityId = cadenceInfo?.entityId;

    switch (recordType.toUpperCase()) {
      case LEAD_OBJECT_TYPE:
        return (
          <LeadDetails
            key={id}
            salesforceLeadStore={salesforceLeadStore}
            id={id}
            fromCadence={true}
          />
        );
      case BORROWER_OBJECT_TYPE:
        return <BorrowerDetailedScreen key={entityId} id={entityId} fromCadence={true} />;
      case TPO_OBJECT_TYPE:
      case BROKER_OBJECT_TYPE:
        return (
          <LenderDetailedScreen
            key={entityId}
            id={entityId}
            fromCadence={true}
            contactRecordType={recordType}
          />
        );
      default:
        return <Box>Unknown record type</Box>;
    }
  };

  return (
    <Box className={classes.peopleLayoutContainer}>
      <Box className={classes.peopleList}>
        <Box className={classes.peopleListH3}>
          <Typography variant="h6">Prospects</Typography>
        </Box>
        <ul className={classes.peopleListUl}>
          {prospects?.map((person) => {
            const personId = getCorrectId(person);
            const isSelected =
              selectedPerson?.cadenceContactInfo?.leadId === person?.cadenceContactInfo?.leadId ||
              selectedPerson?.cadenceContactInfo?.contactId === person?.cadenceContactInfo?.contactId;

            return (
              <li
                key={personId}
                className={classes.peopleListLi}
                onClick={() => handleSelectPerson(person)}
              >
                <Typography>{`${person?.firstName ?? ""} ${person?.lastName ?? ""}`}</Typography>
              </li>
            );
          })}
        </ul>
      </Box>

      <Box className={classes.peopleDetail}>
        {selectedPerson ? (
          renderDetails()
        ) : (
          <Box className={classes.noSelectionMessage}>
            Select a prospect to see details
          </Box>
        )}
      </Box>
    </Box>
  );
});
